<template>
  <AuthCard>
    <p>
      Enter your e-mail address and we'll send you a link to reset your&nbsp;password.
    </p>

    <form @submit.prevent="onSubmit">
      <div v-if="formAlertMessages.length" class="c-login__error">
        <Alert
          v-for="(msg, i) in formAlertMessages"
          :key="i"
          :type="msg.type"
          @close="formAlertMessages.splice(i, 1)"
        >
          {{ msg.message }}
        </Alert>
      </div>

      <div class="c-login__input">
        <TextInput
          name="email"
          label="E-mail"
          input-type="email"
          autocomplete="email"
          placeholder="E-mail"
          hide-label
          required
          autofocus
          :model-value="form.email"
          :errors="getFieldErrors('email')"
          data-test="password-reset-email"
          @update:model-value="form.email = $event"
        />
      </div>
      <Alert
        v-if="ssoRedirectUrl"
        type="info"
        :dismissible="false"
        class="mt-3 mb-0"
        data-test="password-reset-sso-note"
      >
        <small>
          Your organization uses Single Sign-On to log into PEARS, so there is no need to reset your password here. If you are having problems signing in with your organization's SSO, then please contact your organization's IT help desk.
        </small>
      </Alert>
      <button
        v-if="ssoRedirectUrl"
        type="button"
        class="btn btn-primary mt-4 w-100"
        :disabled="redirecting"
        data-test="password-reset-sso-submit"
        @click="onSsoRedirect"
      >
        {{ redirecting ? 'Redirecting…' : 'Sign in with SSO' }}
      </button>
      <button
        v-else
        type="submit"
        class="btn btn-primary mt-4 w-100"
        :disabled="submitting"
        data-test="password-reset-submit"
      >
        {{ submitting ? 'Sending…' : 'Reset My Password' }}
      </button>
      <div class="text-center mt-3">
        <router-link :to="{name: 'signin'}" data-test="password-reset-back-to-signin">
          <small>Back to Sign In</small>
        </router-link>
      </div>
    </form>
  </AuthCard>
</template>

<script setup>
import {computed, inject, reactive, ref} from 'vue';
import {useRouter} from 'vue-router';
import * as Sentry from '@sentry/browser';
import AuthCard from './components/AuthCard.vue';
import Alert from '@canopyllc/roots/components/Alert.vue';
import TextInput from '@canopyllc/roots/components/forms/TextInput.vue';
import {lookupSsoDomain, redirectToSamlLogin} from '../../utils/ssoRedirect.js';
import {useErrorManager} from '@canopyllc/roots/composables/errorManager.js';

const api = inject('api'),
      urls = inject('urls'),
      emailDomains = inject('emailDomains'),
      router = useRouter(),

      form = reactive({email: ''}),
      submitting = ref(false),
      redirecting = ref(false),

      ssoRedirectUrl = computed(() => lookupSsoDomain(form.email, emailDomains)),

      {
        formAlertMessages, getFieldErrors, clearFormErrors, setNonFieldErrors, handleAllauthErrors,
      } = useErrorManager();

async function onSsoRedirect() {
  if (!ssoRedirectUrl.value) return;
  clearFormErrors();
  redirecting.value = true;
  try {
    await redirectToSamlLogin(ssoRedirectUrl.value);
  } catch (err) {
    Sentry.captureException(err);
    setNonFieldErrors([{message: 'We could not redirect you to your single sign-on provider. Please try again.', type: 'danger'}]);
    redirecting.value = false;
  }
}

async function onSubmit() {
  if (ssoRedirectUrl.value) {
    await onSsoRedirect();
    return;
  }
  clearFormErrors();
  submitting.value = true;
  try {
    await api.post(urls.passwordResetRequestApiUrl, {email: form.email});
    router.push({name: 'password_reset_done'});
  } catch (err) {
    if (err?.response?.status === 400) {
      handleAllauthErrors(err);
    } else {
      Sentry.captureException(err);
      setNonFieldErrors([{message: 'Something went wrong. Please try again.', type: 'danger'}]);
    }
    submitting.value = false;
  }
}
</script>
